"use client";

import { getResources, ResourceCard, type Resource } from "@entities/resources";
import type { FilterSearchParams } from "@entities/resources/ui/filter-bar";
import type { WithTotalCount } from "@shared/api";
import type { Locale } from "@shared/i18n";
import { useBreakpoints } from "@shared/lib/breakpoints";
import { Container } from "@shared/ui/container";
import { Spinner } from "@shared/ui/spinner";
import { useLocale } from "next-intl";
import { useCallback, useMemo, useState, useTransition } from "react";
import { useInView } from "react-intersection-observer";
import { gridSizeMap } from "./config";

export type GridProps = {
  searchParams: FilterSearchParams;
  initialState: WithTotalCount<Resource[]>;
};

export function Grid({ searchParams, initialState }: GridProps) {
  const locale = useLocale() as Locale;

  const [{ data: resources, total }, setResources] =
    useState<WithTotalCount<Resource[]>>(initialState);
  const [page, setPage] = useState(1);
  const [isPending, startTransition] = useTransition();
  const { current } = useBreakpoints();

  const { ref: bottomRef } = useInView({
    onChange: (inView) => {
      if (inView && total > resources.length && !isPending) {
        fetchNextPage();
      }
    },
  });

  const gridSizes = useMemo(
    () => Array.from({ length: gridSizeMap[current] }),
    [current]
  );

  const fetchNextPage = useCallback(
    () =>
      startTransition(async () => {
        const result = await getResources(searchParams, locale, page);

        setResources((prev) => ({
          data: [...prev.data, ...result.data],
          total: result.total,
        }));

        setPage((prev) => prev + 1);
      }),
    [locale, page, searchParams]
  );

  return (
    <Container className={"min-h-fit bg-gray-200"}>
      {resources.length ? (
        <>
          <div
            className={
              "w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
            }
          >
            {gridSizes.map((_, gridIndex, { length: total }) => (
              <div
                key={`grid:${gridIndex}`}
                className="grid place-self-start gap-6"
              >
                {resources
                  .filter((_, index) => (index - gridIndex) % total === 0)
                  .map((resource) => (
                    <ResourceCard
                      key={`resources:${resource.name}`}
                      resource={resource}
                      className="h-fit"
                    />
                  ))}
              </div>
            ))}

            <div ref={bottomRef} className="h-4 w-full" />
          </div>

          {isPending && (
            <Spinner size={"sm"} className={"!w-fit !max-w-fit !h-full"} />
          )}
        </>
      ) : (
        <div className={"text-gray-700 text-lg"}>
          Nenhum conteúdo encontrado
        </div>
      )}
    </Container>
  );
}
